import { Variants } from '@/components/Button/Variants.tsx'
import ExpanderCollapser from '@/components/ExpanderCollapser/ExpanderCollapser.tsx'
import { CONST } from '@/constants.ts'
import { SEO_SPACER } from '@/lib/seoSpacer.ts'
import classnames from 'classnames'
import * as React from 'react'
import NordicLink from '../Button/NordicLink.tsx'
import { UTMLink } from '../UTMLink/UTMLink.tsx'
import { NavDropdown } from './NavDropdown.tsx'
import styles from './Navigation.module.scss'

const PRODUCT_SUBMENU_PATHS = {
	'/device-management/': 'Device Management',
	'/device-observability/': 'Device Observability',
	'/location-services/': 'Location Services',
	'/security-services/': 'Security Services',
} as const

const SOLUTIONS_SUBMENU_PATHS = {
	'/lifetime-fota/': 'Lifetime FOTA',
	'/cra-readiness-assessment/': 'CRA Readiness Assessment',
} as const

const PRICING_SUBMENU_PATHS = {
	'/pricing/': 'Pricing Overview',
	'/pricing/calculator/': 'Pricing Calculator',
} as const

const UTM_CAMPAIGNS_BY_PATHNAME = {
	'/': CONST.UTM_CAMPAIGNS.HOME,
	'/device-management/': CONST.UTM_CAMPAIGNS.DEVICE_MANAGEMENT,
	'/device-observability/': CONST.UTM_CAMPAIGNS.DEVICE_OBSERVABILITY,
	'/lifetime-fota/': CONST.UTM_CAMPAIGNS.LIFETIME_FOTA,
	'/location-services/': CONST.UTM_CAMPAIGNS.LOCATION_SERVICES,
	'/pricing/': CONST.UTM_CAMPAIGNS.PRICING,
	'/pricing/calculator/': CONST.UTM_CAMPAIGNS.PRICING_CALCULATOR,
	'/security-services/': CONST.UTM_CAMPAIGNS.SECURITY_SERVICES,
} as const

/**
 * URL for the new nRF Cloud documentation site.
 *
 * NOTE: There was previously a 301 redirect from docs.nrfcloud.com
 * to docs.nordicsemi.com. The `?new` query param is included to
 * bust the cache in any browsers that have previously visited
 * docs.nrfcloud.com while the redirect was in place.
 */
const NRFCLOUD_DOCS_URL = `${CONST.NRFCLOUD_DOCS_URL}/?new`

const getRegisterUtmParams = (pathname: string) => {
	return {
		utm_source: CONST.UTM_SOURCE,
		utm_medium: 'direct',
		utm_campaign:
			UTM_CAMPAIGNS_BY_PATHNAME[pathname] ?? CONST.UTM_CAMPAIGNS.HOME,
		utm_content: 'nav-cta',
	}
}
type DesktopNavProps = {
	isDarkNav?: boolean
	pathname: string
}

/** Nav menu shown on larger screens (above md breakpoint) */
const DesktopNav: React.FC<DesktopNavProps> = ({ isDarkNav, pathname }) => {
	const registerUtmParams = getRegisterUtmParams(pathname)

	return (
		<nav
			className={classnames(styles.navigation, {
				[styles.darkNavigation]: isDarkNav,
			})}
		>
			<ul className={styles.leftNav}>
				<li className={styles.nordicBrandDesktop}>
					<a className={styles.nordicBrand} href="/">
						<img
							src={
								isDarkNav
									? '/img/nRF-Cloud-black-text.svg'
									: '/img/nRF-Cloud-white-text.svg'
							}
							alt="nRF Cloud"
						/>
					</a>
				</li>
				<li>
					<NavDropdown
						className={styles.link}
						content={
							<ul>
								{Object.entries(PRODUCT_SUBMENU_PATHS).map(([path, label]) => (
									<li key={path}>
										<a href={path}>
											{label}
											{SEO_SPACER}
										</a>
									</li>
								))}
							</ul>
						}
					>
						Product
					</NavDropdown>
				</li>
				<li>
					<NavDropdown
						className={styles.link}
						content={
							<ul>
								{Object.entries(SOLUTIONS_SUBMENU_PATHS).map(
									([path, label]) => (
										<li key={path}>
											<a href={path}>
												{label}
												{SEO_SPACER}
											</a>
										</li>
									),
								)}
							</ul>
						}
					>
						Solutions
					</NavDropdown>
				</li>
				<li>
					<NavDropdown
						className={styles.link}
						content={
							<ul>
								{Object.entries(PRICING_SUBMENU_PATHS).map(([path, label]) => (
									<li key={path}>
										<a href={path}>
											{label}
											{SEO_SPACER}
										</a>
									</li>
								))}
							</ul>
						}
					>
						Pricing
					</NavDropdown>
				</li>
				<li>
					<a
						href={NRFCLOUD_DOCS_URL}
						target="_blank"
						rel="noopener noreferrer"
						className={classnames(styles.link)}
					>
						Documentation
					</a>
				</li>
			</ul>
			<ul className={styles.rightNav}>
				<li>
					<NordicLink
						variant={Variants.secondaryNeutral}
						data-cy="login-modal-trigger"
						href={CONST.LOGIN_URL}
					>
						Sign in
					</NordicLink>
				</li>
				<li>
					<NordicLink
						showSlant
						variant={Variants.warning}
						data-cy="create-account-button"
						href={CONST.REGISTER_URL}
						preserveUtmParams
						utmParams={registerUtmParams}
						data-testid="nav-cta"
					>
						Register
					</NordicLink>
				</li>
			</ul>
		</nav>
	)
}

type MobileNavProps = {
	showMobileNav: boolean
	setShowMobileNav: (value: boolean) => void
	pathname: string
}

/** Nav menu shown on mobile screens (below md breakpoint) */
const MobileNav: React.FC<MobileNavProps> = ({
	showMobileNav,
	setShowMobileNav,
	pathname,
}) => {
	const isOnProductPage = pathname in PRODUCT_SUBMENU_PATHS
	const isOnSolutionsPage = pathname in SOLUTIONS_SUBMENU_PATHS
	const isOnPricingPage = pathname in PRICING_SUBMENU_PATHS
	const registerUtmParams = getRegisterUtmParams(pathname)

	return (
		<nav className={classnames({ [styles.mobileShown]: showMobileNav })}>
			<ul>
				<li className={styles.mobileSubmenu}>
					<ExpanderCollapser
						toggleClass={styles.mobileSubmenuToggleRow}
						animationTime="0.3s"
						topButton={{
							initState: (
								<span className={styles.mobileSubmenuToggleContent}>
									Product
									<i className="material-icons">expand_more</i>
								</span>
							),
							altState: (
								<span className={styles.mobileSubmenuToggleContent}>
									Product
									<i className="material-icons">expand_less</i>
								</span>
							),
							persist: true,
						}}
						defaultOpen={isOnProductPage}
					>
						<ul className={styles.mobileSubmenu}>
							{Object.entries(PRODUCT_SUBMENU_PATHS).map(([path, label]) => (
								<li key={path}>
									<a
										href={path}
										className={classnames({
											[styles.active]: pathname === path,
										})}
										tabIndex={pathname === path ? -1 : undefined}
										onClick={() => {
											setShowMobileNav(false)
										}}
									>
										<span>{label}</span>
									</a>
								</li>
							))}
						</ul>
					</ExpanderCollapser>
					<hr />
				</li>
				<li className={styles.mobileSubmenu}>
					<ExpanderCollapser
						toggleClass={styles.mobileSubmenuToggleRow}
						animationTime="0.3s"
						topButton={{
							initState: (
								<span className={styles.mobileSubmenuToggleContent}>
									Solutions
									<i className="material-icons">expand_more</i>
								</span>
							),
							altState: (
								<span className={styles.mobileSubmenuToggleContent}>
									Solutions
									<i className="material-icons">expand_less</i>
								</span>
							),
							persist: true,
						}}
						defaultOpen={isOnSolutionsPage}
					>
						<ul className={styles.mobileSubmenu}>
							{Object.entries(SOLUTIONS_SUBMENU_PATHS).map(([path, label]) => (
								<li key={path}>
									<a
										href={path}
										className={classnames({
											[styles.active]: pathname === path,
										})}
										tabIndex={pathname === path ? -1 : undefined}
										onClick={() => {
											setShowMobileNav(false)
										}}
									>
										<span>{label}</span>
									</a>
								</li>
							))}
						</ul>
					</ExpanderCollapser>
					<hr />
				</li>
				<li className={styles.mobileSubmenu}>
					<ExpanderCollapser
						toggleClass={styles.mobileSubmenuToggleRow}
						animationTime="0.3s"
						topButton={{
							initState: (
								<span className={styles.mobileSubmenuToggleContent}>
									Pricing
									<i className="material-icons">expand_more</i>
								</span>
							),
							altState: (
								<span className={styles.mobileSubmenuToggleContent}>
									Pricing
									<i className="material-icons">expand_less</i>
								</span>
							),
							persist: true,
						}}
						defaultOpen={isOnPricingPage}
					>
						<ul className={styles.mobileSubmenu}>
							{Object.entries(PRICING_SUBMENU_PATHS).map(([path, label]) => (
								<li key={path}>
									<a
										href={path}
										className={classnames({
											[styles.active]: pathname === path,
										})}
										tabIndex={pathname === path ? -1 : undefined}
										onClick={() => {
											setShowMobileNav(false)
										}}
									>
										<span>{label}</span>
									</a>
								</li>
							))}
						</ul>
					</ExpanderCollapser>
					<hr />
				</li>
				<li>
					<a
						href={NRFCLOUD_DOCS_URL}
						target="_blank"
						onClick={() => {
							setShowMobileNav(false)
						}}
					>
						Documentation
					</a>
					<hr />
				</li>
				<li>
					<UTMLink
						href={CONST.REGISTER_URL}
						className={styles.link}
						preserveUtmParams
						utmParams={registerUtmParams}
						data-testid="nav-cta"
					>
						<span>Register</span>
					</UTMLink>
					<hr />
				</li>
				<li>
					<a href={CONST.LOGIN_URL} className={styles.link}>
						<span>Sign in</span>
					</a>
				</li>
			</ul>
		</nav>
	)
}

export type NavigationContentProps = {
	isDarkNav?: boolean
}

export const NavigationContent: React.FC<NavigationContentProps> = ({
	isDarkNav,
}) => {
	const [showMobileNav, setShowMobileNav] = React.useState(false)

	const toggleMobileNav = () => {
		setShowMobileNav(!showMobileNav)
	}
	const pathname = typeof window !== 'undefined' ? window.location.pathname : ''

	return (
		<>
			<div
				className={classnames(styles.navigationMobile, {
					[styles.darkNavigationMobile]: isDarkNav,
					[styles.lightNavigationMobile]: !isDarkNav,
				})}
			>
				<div className={styles.navigationMobileHeader}>
					<div className={styles.mobileLogo}>
						<a className={styles.nordicBrandMobile} href="/">
							<img
								src={
									isDarkNav
										? '/img/nRF-Cloud-black-text.svg'
										: '/img/nRF-Cloud-white-text.svg'
								}
								alt="nRF Cloud"
							/>
						</a>
					</div>
					<div className={styles.mobileHamburger}>
						<button className={styles.menu} onClick={toggleMobileNav}>
							<img
								src={showMobileNav ? '/img/x.svg' : '/img/hamburger.svg'}
								alt="menu hamburger"
							/>
						</button>
					</div>
				</div>
				{/* THIS IS THE ACTUAL MOBILE MENU */}
				{showMobileNav && (
					<MobileNav
						showMobileNav={showMobileNav}
						setShowMobileNav={setShowMobileNav}
						pathname={pathname}
					/>
				)}
			</div>
			<DesktopNav isDarkNav={isDarkNav} pathname={pathname} />
		</>
	)
}
